import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CartButton from '../../../components/CartButton';

export default function ShopNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToCategory = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/98 backdrop-blur-lg shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4 border-b border-stone-100'}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group relative">
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <p className="text-stone-900 font-display text-xl tracking-wide">House of FMR</p>
              <p className="text-amber-600 text-[10px] tracking-[0.25em] uppercase font-medium">Flair • Mixology • Rentals</p>
            </div>
            {/* FMR Tooltip */}
            <div className="absolute top-full left-0 mt-2 px-3 py-1.5 bg-stone-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Flair • Mixology • Rentals
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {[
              { id: 'shop-classes', label: 'Mixology Classes' },
              { id: 'shop-kits', label: 'Cocktail Kits' },
              { id: 'shop-manuals', label: 'Recipe Manuals' },
              { id: 'shop-packages', label: 'Event Packages' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToCategory(item.id)}
                className="text-sm font-medium text-stone-600 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
            <Link 
              to="/services" 
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/services') ? 'text-amber-600 font-semibold' : 'text-stone-600 hover:text-amber-600'
              }`}
            >
              Services
            </Link>
            <Link 
              to="/equipment-hire" 
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/equipment-hire') ? 'text-amber-600 font-semibold' : 'text-stone-600 hover:text-amber-600'
              }`}
            >
              Equipment Hire
            </Link>
            <Link 
              to="/gallery" 
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/gallery') ? 'text-amber-600 font-semibold' : 'text-stone-600 hover:text-amber-600'
              }`}
            >
              Gallery
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <CartButton />
            <Link
              to="/wishlist"
              className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 hover:bg-stone-200 transition-colors cursor-pointer"
              aria-label="Wishlist"
            >
              <i className="ri-heart-line text-lg"></i>
            </Link>
            <a
              href="tel:0411164144"
              className="flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              0411 164 144
            </a>
            <Link
              to="/"
              className="px-6 py-2.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/30 border border-amber-300/20"
            >
              Back to Home
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <CartButton />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-900 cursor-pointer"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-white rounded-2xl p-5 shadow-2xl border border-amber-100">
            <div className="space-y-1">
              {[
                { id: 'shop-classes', label: 'Mixology Classes', icon: 'ri-graduation-cap-line' },
                { id: 'shop-kits', label: 'Cocktail Kits', icon: 'ri-gift-line' },
                { id: 'shop-manuals', label: 'Recipe Manuals', icon: 'ri-book-open-line' },
                { id: 'shop-packages', label: 'Event Packages', icon: 'ri-box-3-line' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToCategory(item.id)}
                  className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${item.icon} text-base`}></i>
                  {item.label}
                </button>
              ))}
              <div className="h-px bg-stone-200 my-2"></div>
              <Link to="/" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-home-line text-base"></i>Home
              </Link>
              <Link to="/services" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-service-line text-base"></i>Services
              </Link>
              <Link to="/equipment-hire" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-calendar-check-line text-base"></i>Equipment Hire
              </Link>
              <Link to="/gallery" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-image-line text-base"></i>Gallery
              </Link>
              <Link to="/wishlist" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-heart-line text-base"></i>Wishlist
              </Link>
              <div className="h-px bg-stone-200 my-2"></div>
              <a
                href="tel:0411164144"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-base"></i>Call 0411 164 144
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}